<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useHead } from '@unhead/vue';
import { parseISO } from 'date-fns';
import { type DeepPartial } from '@/shared/types';
import {
  useI18nStore,
  setI18nLanguage,
  LOCALES,
  DEFAULT_LOCALE,
  useI18n,
} from '@/app/modules/i18n';
import {
  ObConfigProvider,
  ObToasts,
  ObConfirmDialog,
  type Config,
  UiConfigProvider,
  type UiConfig,
} from '@/ui-kit';
const i18nStore = useI18nStore();
const { t, n, d } = useI18n();
const uiConfig = computed<Config>(() => ({
  numberFormat: {
    decimalSeparator: i18nStore.decimalSeparator,
    thousandSeparator: i18nStore.thousandSeparator,
  },
  i18n: {
    BatchActionsMenu: {
      labelSelectedItems(count) {
        return t('common.messages.itemsSelected', count);
      },
    },
    FileUploader: {
      labelTrigger: t('ui.fileUploader.labelTrigger'),
    },
    TablePagination: {
      labelRowsPerPage: t('ui.TablePagination.labelRowsPerPage'),
      labelDisplayedRows(currentPageStart, currentPageEnd, total) {
        return t('ui.TablePagination.labelDisplayedRows', {
          min: n(currentPageStart, 'pretty'),
          max: n(currentPageEnd, 'pretty'),
          total: n(total, 'pretty'),
        });
      },
    },
    DatePicker: {
      label(value, selectionMode) {
        if (selectionMode === 'range') {
          const { start, end } = value as any;
          if (start && end) {
            return `${d(parseISO(start), 'date')} - ${d(parseISO(end), 'date')}`;
          }
          if (start) {
            return `${d(parseISO(start), 'date')} ...`;
          }
          return 'Select dates';
        }
        if (typeof value === 'string') {
          return `${d(parseISO(value), 'date')}`;
        }
        return 'Select date';
      },
    },
  },
}));
const uiConfigNew = computed<DeepPartial<UiConfig>>(() => ({
  i18n: {
    dataTable: {
      columnsSettingsTitle: t('common.messages.columnsVisibilitySelectionHint'),
      columnsSettingsSearchHint: t('common.messages.columnsSearchHint'),
      openColumnsSettings: t('common.actions.openColumnsVisibilitySettings'),
      noData: 'No data',
      itemsSelected: (count) =>
        `${n(count, 'pretty')} ${t('common.messages.itemsSelected', count)}`,
      selectAllItems: (count) =>
        ` ${t('common.actions.selectAll')} ${n(count, 'pretty')} ${t('common.plurals.items', count)}`,
      clearSelection: t('common.actions.clearSelection'),
      sortAscending: t('ui.sortAscending'),
      sortDescending: t('ui.sortDescending'),
      pinColumn: t('ui.pinColumn'),
      unpinColumn: t('ui.unpinColumn'),
      hideColumn: t('ui.hideColumn'),
    },
  },
}));
onMounted(() => {
  let locale = i18nStore.locale || window.navigator.language;
  if (!LOCALES.find(({ code }) => code === locale)) {
    locale = DEFAULT_LOCALE;
  }
  setI18nLanguage(locale);
  i18nStore.setLocale(locale);
});
useHead(
  computed(() => ({
    htmlAttrs: {
      lang: i18nStore.locale,
      dir: i18nStore.textDirection,
    },
    titleTemplate: (value) => `${value ? value + ' | ' : ''}Onebeat Demo`,
  })),
);
</script>

<template>
  <UiConfigProvider :config="uiConfigNew">
    <ObConfigProvider :config="uiConfig">
      <RouterView />
      <ObToasts />
      <ObConfirmDialog />
    </ObConfigProvider>
  </UiConfigProvider>
</template>

<style>
body {
  font-family: 'Poppins', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--ui2-background-tone-5, #f5f5f5);
  margin: 0;
  min-width: 1400px;
  font-optical-sizing: auto;
}

#app {
  position: relative;
  z-index: 1;
}

/* Dark sidebar theme (e.g. Lavender) */
.theme-dark-sidebar {
  --ui2-sidebar-text: #f5f5f5;
  --ui2-sidebar-text-secondary: rgba(245, 245, 245, 0.6);
  --ui2-sidebar-text-muted: rgba(245, 245, 245, 0.4);
  --ui2-sidebar-hover: rgba(255, 255, 255, 0.08);
  --ui2-sidebar-active: rgba(255, 255, 255, 0.12);
  --ui2-sidebar-separator: rgba(255, 255, 255, 0.12);
  --ui2-action-panel-text: #f5f5f5;
  --ui2-action-panel-btn-bg: rgba(255, 255, 255, 0.12);
  --ui2-action-panel-btn-border: rgba(255, 255, 255, 0.2);
  --ui2-action-panel-btn-hover: rgba(255, 255, 255, 0.18);
  --ui2-action-panel-separator: rgba(255, 255, 255, 0.12);
}
</style>
